import React, { useEffect, useState, useMemo } from "react";
import { useCallSession } from "../context/CallSessionContext";
import GlobalCallBar from "./GlobalCallBar";

function GlobalCallShellInner() {
  const callSession = useCallSession();
  const state = callSession?.state || {};
  const actions = callSession?.actions || {};
  const refs = callSession?.refs || {};

  const {
    callState,
    activePeer,
    isMuted,
    isSpeakerOn,
    callDuration,
    callProgressLabel,
    bluetoothAvailable,
    audioRoute,
  } = state;

  const { endCall, toggleMute, toggleSpeaker, selectAudioRoute } = actions;
  const { remoteAudioRef } = refs;

  const [isExpanded, setIsExpanded] = useState(false);

  const hasActiveCall = callState && callState !== "idle";

  // Intentionally silent: this component re-renders frequently during calls.

  useEffect(() => {
    if (hasActiveCall) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [hasActiveCall]);

  const stablePeer = useMemo(
    () => activePeer || null,
    [activePeer]
  );
  const peerRank = stablePeer?.rank ? `${stablePeer.rank} ` : "";
  const peerName = stablePeer?.name || "";
  const peerLabel = `${peerRank}${peerName}`.trim();
  const peerAvatar = stablePeer?.avatarUrl || "/avatar-image.png";

  // Always ensure there's a global audio sink while a call is active
  const shouldRenderAudio = callState && callState !== "idle" && remoteAudioRef;

  if (!hasActiveCall && !shouldRenderAudio) return null;

  return (
    <>
      {shouldRenderAudio && (
        <audio
          ref={remoteAudioRef}
          autoPlay
          playsInline
          style={{
            width: 1,
            height: 1,
            opacity: 0.01,
            position: "absolute",
            bottom: 0,
            left: 0,
            zIndex: 1,
          }}
        />
      )}

      {hasActiveCall && (
        isExpanded ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              backgroundColor: "rgba(0,0,0,0.9)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#e5e7eb",
                padding: "24px",
                textAlign: "center",
              }}
            >
              <div style={{ marginBottom: 16, fontSize: "1.4rem", fontWeight: 600 }}>
                {callState === "ringing_outgoing" && stablePeer && (
                  <>{callProgressLabel || "Ringing..."}</>
                )}
                {callState === "connected" && stablePeer && (
                  <>{callProgressLabel || "Connected"}</>
                )}
                {(!stablePeer || !callState) && "Connecting..."}
              </div>

              {stablePeer && (callState === "ringing_outgoing" || callState === "connected") && (
                <>
                  <img
                    src={peerAvatar}
                    alt={`${stablePeer.name || "User"} avatar`}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = "/avatar-image.png";
                    }}
                    style={{
                      width: 104,
                      height: 104,
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginBottom: 16,
                      border: "3px solid rgba(255,255,255,0.18)",
                      boxShadow: "0 14px 30px rgba(0,0,0,0.28)",
                      backgroundColor: "#1f2937",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {peerLabel || stablePeer.name}
                  </div>
                </>
              )}

              {stablePeer?.agency && (
                <div
                  style={{
                    fontSize: "1rem",
                    opacity: 0.8,
                    marginBottom: 12,
                  }}
                >
                  {stablePeer.agency}
                </div>
              )}

              {callState === "connected" && (
                <div
                  style={{
                    fontSize: "1rem",
                    opacity: 0.9,
                    marginBottom: 24,
                  }}
                >
                  {Math.floor((callDuration || 0) / 60)
                    .toString()
                    .padStart(2, "0")}
                  :
                  {((callDuration || 0) % 60)
                    .toString()
                    .padStart(2, "0")}
                </div>
              )}

              <div
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {callState === "connected" && (
                    <button
                      onClick={toggleMute}
                      style={{
                        minWidth: 90,
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: "none",
                        backgroundColor: isMuted ? "#fbbf24" : "#4b5563",
                        color: "#fff",
                        fontSize: "0.9rem",
                      }}
                    >
                      {isMuted ? "Unmute" : "Mute"}
                    </button>
                  )}

                  {callState === "connected" && (
                    <button
                      onClick={toggleSpeaker}
                      style={{
                        minWidth: 110,
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: "none",
                        backgroundColor: isSpeakerOn ? "#3b82f6" : "#4b5563",
                        color: "#fff",
                        fontSize: "0.9rem",
                      }}
                    >
                      {isSpeakerOn ? "Speaker ON" : "Speaker OFF"}
                    </button>
                  )}

                  {callState === "connected" && (
                    <button
                      onClick={() => bluetoothAvailable && selectAudioRoute?.("bluetooth")}
                      disabled={!bluetoothAvailable}
                      style={{
                        minWidth: 110,
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: "none",
                        backgroundColor:
                          audioRoute === "bluetooth" ? "#10b981" : "#4b5563",
                        color: "#fff",
                        fontSize: "0.9rem",
                        opacity: bluetoothAvailable ? 1 : 0.45,
                        cursor: bluetoothAvailable ? "pointer" : "not-allowed",
                      }}
                    >
                      Bluetooth
                    </button>
                  )}

                  <button
                    onClick={endCall}
                    style={{
                      minWidth: 90,
                      padding: "10px 14px",
                      borderRadius: 999,
                      border: "none",
                      backgroundColor: "#ef4444",
                      color: "#fff",
                      fontSize: "0.9rem",
                    }}
                  >
                    End
                  </button>
                </div>

                {callState === "connected" && (
                  <button
                    onClick={() => setIsExpanded(false)}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 999,
                      border: "none",
                      fontSize: "0.9rem",
                      backgroundColor: "rgba(31,41,55,0.9)", // slate-800
                      color: "#e5e7eb", // text-gray-200
                      boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
                      cursor: "pointer",
                    }}
                  >
                    Minimize
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <GlobalCallBar
            peer={stablePeer || { name: "Call in progress", agency: "" }}
            callDuration={callDuration}
            isMuted={isMuted}
            onToggleMute={toggleMute}
            onEnd={endCall}
            onExpand={() => setIsExpanded(true)}
          />
        )
      )}
    </>
  );
}

const GlobalCallShell = React.memo(GlobalCallShellInner);

export default GlobalCallShell;
